import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, query, where, serverTimestamp, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LeagueStat, LeagueStatControl, Player } from '@/types';
import { Eye, EyeOff, Save, CircleDot, Plus } from 'lucide-react';

const slug = (s = '') => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

type Controls = Record<string, LeagueStatControl>;

// Same season sort as the public profile so the list reads newest-first.
const seasonSortKey = (s = ''): number => {
  const y = parseInt((s.match(/\d{4}/) || ['0'])[0], 10);
  const frac = /winter/i.test(s) ? 0.95 : /fall/i.test(s) ? 0.72 : /summer/i.test(s) ? 0.5 : /spring/i.test(s) ? 0.25 : 0;
  return y + frac;
};

const SOURCE_LABEL: Record<LeagueStat['source'], string> = {
  usbc: 'USBC',
  leaguesecretary: 'LeagueSecretary',
  manual: 'Manual',
};

// Every historical league row (USBC + LeagueSecretary) for a bowler, with a hide
// toggle (kills cross-source dupes) and a string-pins flag (shown to recruiters).
// Choices live on player.leagueStatControls (coach-owned) so the nightly sync,
// which only rewrites the leagueStats docs themselves, never overwrites them.
export const LeagueHistoryPanel: React.FC<{ player: Player }> = ({ player }) => {
  const [rows, setRows] = useState<LeagueStat[]>([]);
  const [controls, setControls] = useState<Controls>(player.leagueStatControls || {});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');

  // Manual-row entry (leagues LeagueSecretary/USBC don't carry — e.g. youth doubles)
  const [showAdd, setShowAdd] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const emptyManual = { league: '', season: '', center: '', average: '', games: '', highGame: '', highSeries: '' };
  const [manual, setManual] = useState(emptyManual);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'leagueStats'), where('playerId', '==', player.id)));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as LeagueStat);
        data.sort((a, b) => seasonSortKey(b.season) - seasonSortKey(a.season) || a.league.localeCompare(b.league));
        setRows(data);
      } catch (e) {
        console.error('leagueStats load failed', e);
        setMessage('Could not load league history, check console.');
      } finally {
        setLoading(false);
      }
    })();
  }, [player.id]);

  const setControl = (id: string, patch: Partial<LeagueStatControl>) => {
    setControls((prev) => {
      const merged = { ...prev[id], ...patch };
      const next = { ...prev };
      // Drop empty control objects so we don't persist noise
      if (!merged.hidden && !merged.stringPins) delete next[id];
      else next[id] = merged;
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await updateDoc(doc(db, 'players', player.id), {
        leagueStatControls: controls,
        updatedAt: serverTimestamp(),
      });
      setDirty(false);
      setMessage('Saved.');
    } catch (e) {
      console.error('leagueStatControls save failed', e);
      setMessage('Save failed. Check console.');
    } finally {
      setSaving(false);
    }
  };

  // Write a source:'manual' leagueStats doc. Deterministic id keyed on season+league
  // so re-saving the same season updates rather than duplicates; the nightly prune
  // only removes source:'leaguesecretary' rows, so manual rows persist.
  const handleAddManual = async () => {
    if (!manual.league.trim() || !manual.season.trim()) {
      setMessage('League and season are required for a manual row.');
      return;
    }
    setAddSaving(true);
    setMessage('');
    const num = (v: string) => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    };
    try {
      const id = `manual_${player.id}_${slug(manual.season)}_${slug(manual.league)}`;
      await setDoc(doc(db, 'leagueStats', id), {
        playerId: player.id,
        ...(player.usbcId ? { usbcId: player.usbcId } : {}),
        league: manual.league.trim(),
        season: manual.season.trim(),
        center: manual.center.trim() || null,
        average: num(manual.average),
        games: num(manual.games),
        highGame: num(manual.highGame),
        highSeries: num(manual.highSeries),
        isComposite: false,
        source: 'manual',
        lastUpdated: serverTimestamp(),
      });
      setManual(emptyManual);
      setShowAdd(false);
      setMessage('Manual season added.');
      // reload rows
      const snap = await getDocs(query(collection(db, 'leagueStats'), where('playerId', '==', player.id)));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as LeagueStat);
      data.sort((a, b) => seasonSortKey(b.season) - seasonSortKey(a.season) || a.league.localeCompare(b.league));
      setRows(data);
    } catch (e) {
      console.error('manual leagueStats add failed', e);
      setMessage('Could not add the manual season, check console.');
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl shadow-tiger p-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-2xl tracking-wide text-tiger-primary-black uppercase">Season History</h2>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 px-4 py-2 bg-tiger-primary-black text-white rounded-lg font-bold text-sm hover:bg-tiger-neutral-800 transition-colors disabled:opacity-40"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : dirty ? 'Save history' : 'Saved'}
        </button>
      </div>
      <p className="text-sm text-tiger-neutral-500 mt-1 mb-4">
        Every season row behind {player.name}'s "Season by Season" and USBC record. The same season can appear from two
        sources (USBC has the average, LeagueSecretary has the high game), <strong>hide</strong> the duplicate you don't
        want. Mark <strong>string pins</strong> so recruiters see the scoring context. Changes stick through the nightly
        sync.
      </p>

      {message && (
        <div
          className={`px-4 py-2 rounded-lg font-bold text-sm mb-4 ${
            message === 'Saved.' ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'
          }`}
        >
          {message}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-tiger-neutral-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-tiger-neutral-500 italic">
          No league history yet. It populates from the USBC and LeagueSecretary syncs once this bowler has a USBC # and a
          name match.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const ctl = controls[r.id] || {};
            const hidden = !!ctl.hidden;
            return (
              <div
                key={r.id}
                className={`border rounded-xl p-4 flex items-start justify-between gap-3 flex-wrap transition-colors ${
                  hidden ? 'border-tiger-neutral-200 bg-tiger-neutral-50 opacity-70' : 'border-tiger-neutral-200'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-tiger-primary-black">{r.league}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-tiger-neutral-100 text-tiger-neutral-600">
                      {SOURCE_LABEL[r.source]}
                    </span>
                    {r.isComposite && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-willard-taupe text-tiger-primary-black">
                        Book composite
                      </span>
                    )}
                    {ctl.stringPins && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                        String pins
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-tiger-neutral-500 mt-0.5">
                    {r.season}
                    {r.center ? ` · ${r.center}` : ''}
                  </div>
                  <div className="text-sm text-tiger-neutral-600 mt-1">
                    avg <span className="font-bold">{r.average}</span> · {r.games} games
                    {r.highGame ? ` · HG ${r.highGame}` : ' · no high game'}
                    {r.highSeries ? ` · HS ${r.highSeries}` : ''}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => setControl(r.id, { hidden: !hidden })}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-tiger-neutral-600 hover:text-tiger-primary-black"
                  >
                    {hidden ? (
                      <>
                        <EyeOff className="w-4 h-4" /> Hidden, show
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" /> Shown, hide
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setControl(r.id, { stringPins: !ctl.stringPins })}
                    className={`inline-flex items-center gap-1.5 text-sm font-bold ${
                      ctl.stringPins ? 'text-purple-700' : 'text-tiger-neutral-500 hover:text-tiger-primary-black'
                    }`}
                  >
                    <CircleDot className="w-4 h-4" /> {ctl.stringPins ? 'String pins ✓' : 'Mark string pins'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add a manual season — for leagues LeagueSecretary and USBC don't carry */}
      <div className="mt-4">
        {!showAdd ? (
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
          >
            <Plus className="w-4 h-4" /> Add a manual season
          </button>
        ) : (
          <div className="border border-tiger-neutral-200 rounded-xl p-4 space-y-3">
            <div>
              <h3 className="font-bold text-tiger-neutral-700">Add a manual season</h3>
              <p className="text-xs text-tiger-neutral-500">
                For a league neither LeagueSecretary nor USBC tracks (e.g. a youth doubles league). This row shows in
                Season by Season like any other and survives the nightly sync.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ['league', 'League', 'Summer Youth Doubles'],
                ['season', 'Season', 'Summer 2026'],
                ['center', 'Center', 'Sunshine Lanes'],
                ['average', 'Average', '175'],
                ['games', 'Games', '18'],
                ['highGame', 'High game', '246'],
                ['highSeries', 'High series', '640'],
              ].map(([key, label, ph]) => (
                <div key={key}>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">{label}</label>
                  <input
                    className="w-full px-3 py-2 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black text-sm"
                    value={manual[key as keyof typeof manual]}
                    onChange={(e) => setManual((m) => ({ ...m, [key]: e.target.value }))}
                    placeholder={ph}
                    inputMode={['average', 'games', 'highGame', 'highSeries'].includes(key) ? 'numeric' : undefined}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddManual}
                disabled={addSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-tiger-primary-black text-white rounded-lg font-bold text-sm hover:bg-tiger-neutral-800 disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {addSaving ? 'Adding…' : 'Add season'}
              </button>
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-tiger-neutral-500 hover:text-tiger-primary-black">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
